import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  IoSearchOutline,
  IoDocumentTextOutline,
  IoClipboardOutline,
  IoChatbubbleEllipsesOutline,
  IoCopyOutline,
  IoCloseOutline,
  IoImageOutline,
} from "react-icons/io5";
import { TbTruckDelivery } from "react-icons/tb";
import { FiChevronDown } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  getOrderProcessingList,
  searchOrderProcessing,
  getOrderProcessingStats,
  getOrderProcessingSingle,
  updateOrderProcessingStatus,
} from "../redux/thunk";
import {
  resetUpdateStatus,
  clearSingleOrder,
} from "../redux/orderProcessing/orderProcessingSlice";
import LoadingSpinner from "../components/elements/LoadingSpinner";
import type {
  AdminStatus,
  DeliveryMethod,
  OrderProcessingOrder,
  OrderType,
} from "../types/orderProcessing";

const ITEMS_PER_PAGE = 10;

const ADMIN_STATUS_OPTIONS: { value: AdminStatus | ""; label: string }[] = [
  { value: "", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "readyForApproval", label: "Ready for Approval" },
  { value: "approved", label: "Approved" },
  { value: "cancelled", label: "Cancelled" },
];

function statusPillClass(status: string): string {
  switch (status) {
    case "pending":
      return "bg-yellow-50 text-yellow-700";
    case "processing":
      return "bg-blue-50 text-blue-600";
    case "readyForApproval":
      return "bg-purple-50 text-purple-600";
    case "approved":
      return "bg-green-50 text-green-700";
    case "cancelled":
    case "cancel":
    case "canceled":
      return "bg-red-50 text-red-600";
    case "delivered":
      return "bg-blue-100 text-blue-700";
    case "rider":
      return "bg-purple-100 text-purple-700";
    default:
      return "bg-gray-50 text-gray-600";
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "processing":
      return "Processing";
    case "readyForApproval":
      return "Ready for Approval";
    case "approved":
      return "Approved";
    case "cancelled":
    case "cancel":
    case "canceled":
      return "Cancelled";
    case "delivered":
      return "Delivered";
    case "rider":
      return "With Rider";
    default: {
      // Capitalize first letter for unknown statuses
      const s = status.trim();
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : status;
    }
  }
}

function normalizeOrderStatus(raw: string | null | undefined): string {
  const s = (raw ?? "").trim();
  if (!s) return "";
  // Backend uses "cancel" while admin flow uses "cancelled"
  if (s === "cancel" || s === "canceled") return "cancelled";
  return s;
}

/**
 * Display logic: always use adminStatus for the order processing page.
 */
function getOrderDisplayStatus(order: OrderProcessingOrder): string {
  return order.adminStatus || "pending";
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 0,
  }).format(amount);
}

function computeGamifiedDiscount(order: OrderProcessingOrder | null): number {
  if (!order?.gamified || order.gamified.length === 0) return 0;
  return order.gamified.reduce((sum, g) => {
    if (g.rewardType === "discount" || g.rewardType === "promocode") {
      const val = Number(g.discountAmountApplied ?? g.promoCodeDiscount ?? 0);
      return Number.isFinite(val) ? sum + val : sum;
    }
    return sum;
  }, 0);
}

function hasFreeDelivery(order: OrderProcessingOrder | null): boolean {
  return Boolean(order?.gamified?.some((g) => g.isFreeDeliveryApplied));
}

const OrderProcessing: React.FC = () => {
  const dispatch = useAppDispatch();
  const { orders, stats, singleOrder, updateStatus } = useAppSelector(
    (state) => state.orderProcessing,
  );

  const [activeTab, setActiveTab] = useState<OrderType | "all">("all");
  const [deliveryFilter, setDeliveryFilter] = useState<DeliveryMethod | "all">(
    "all",
  );
  const [searchEmail, setSearchEmail] = useState("");
  const [statusFilter, setStatusFilter] = useState<AdminStatus | "">("");
  const [page, setPage] = useState(1);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [statusDropdownOrderId, setStatusDropdownOrderId] = useState<
    string | null
  >(null);

  const isClientPaginated =
    activeTab !== "all" || deliveryFilter !== "all" || Boolean(statusFilter);

  const fetchOrders = useCallback(() => {
    if (searchEmail.trim()) {
      dispatch(
        searchOrderProcessing({
          status: statusFilter || "pending",
          email: searchEmail.trim(),
        }),
      );
    } else {
      dispatch(
        getOrderProcessingList({
          page: isClientPaginated ? 1 : page,
          limit: isClientPaginated ? 1000 : ITEMS_PER_PAGE,
        }),
      );
    }
  }, [dispatch, page, searchEmail, statusFilter, isClientPaginated]);

  useEffect(() => {
    dispatch(getOrderProcessingStats());
  }, [dispatch]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (updateStatus.status === "success") {
      dispatch(resetUpdateStatus());
      fetchOrders();
      dispatch(getOrderProcessingStats());
    }
  }, [updateStatus.status, dispatch, fetchOrders]);

  const filteredOrders = useMemo(() => {
    let result = orders.data;
    if (activeTab !== "all") {
      result = result.filter((o) => o.orderType === activeTab);
    }
    if (deliveryFilter !== "all") {
      result = result.filter((o) => {
        const isPickup = o.customerMode?.toLowerCase() === "pickup";
        return deliveryFilter === "pickup" ? isPickup : !isPickup;
      });
    }
    if (statusFilter) {
      result = result.filter((o) => o.adminStatus === statusFilter);
    }
    return result;
  }, [orders.data, activeTab, deliveryFilter, statusFilter]);

  const displayOrders = useMemo(() => {
    if (!isClientPaginated) return filteredOrders;
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredOrders.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredOrders, isClientPaginated, page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  const handleViewDetails = (orderId: string) => {
    dispatch(getOrderProcessingSingle({ orderId }));
    setDetailModalOpen(true);
  };

  const handleStatusUpdate = (orderId: string, newStatus: AdminStatus) => {
    setStatusDropdownOrderId(null);
    dispatch(updateOrderProcessingStatus({ orderId, adminStatus: newStatus }));
  };

  const copyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
  };

  const totalPages = isClientPaginated
    ? Math.max(1, Math.ceil(filteredOrders.length / ITEMS_PER_PAGE))
    : orders.pagination.totalPages || 1;

  const showingStart =
    displayOrders.length > 0 ? (page - 1) * ITEMS_PER_PAGE + 1 : 0;
  const showingEnd =
    displayOrders.length > 0 ? showingStart + displayOrders.length - 1 : 0;

  const showingTotal = isClientPaginated
    ? filteredOrders.length
    : orders.pagination.total;

  return (
    <main className="w-full overflow-x-auto pb-10">
      <div className="bg-white rounded-md shadow-sm pb-6">
        <div className="w-full overflow-x-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              Order Processing
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage and track all orders
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <StatCard
              label="Total Orders"
              value={stats.data?.totalOrders ?? 0}
              icon={<IoDocumentTextOutline className="w-5 h-5" />}
              loading={stats.status === "loading"}
            />
            <StatCard
              label="Pending Orders"
              value={stats.data?.pendingOrders ?? 0}
              icon={<IoClipboardOutline className="w-5 h-5" />}
              loading={stats.status === "loading"}
            />
            <StatCard
              label="Processing"
              value={stats.data?.processingOrders ?? 0}
              icon={<IoChatbubbleEllipsesOutline className="w-5 h-5" />}
              loading={stats.status === "loading"}
            />
            <StatCard
              label="Ready for Approval"
              value={stats.data?.readyForApprovalOrders ?? 0}
              icon={<TbTruckDelivery className="w-5 h-5" />}
              loading={stats.status === "loading"}
            />
            <StatCard
              label="Approved"
              value={stats.data?.approvedOrders ?? 0}
              icon={<IoClipboardOutline className="w-5 h-5" />}
              loading={stats.status === "loading"}
            />
          </div>

          <div className="flex items-center gap-8 border-b border-gray-100 mb-6">
            {(
              [
                { key: "all", label: "All Orders" },
                { key: "flexible", label: "Flexible Orders" },
                { key: "outright", label: "Outright Orders" },
                { key: "combo", label: "Combo Packs" },
              ] as { key: OrderType | "all"; label: string }[]
            ).map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? "border-green-600 text-green-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => {
                  setActiveTab(tab.key);
                  setPage(1);
                }}
              >
                {tab.label}
              </button>
            ))}

            <div className="w-px h-6 bg-gray-200 mx-1 self-center" />

            {(
              [
                { key: "home-delivery", label: "Home Delivery" },
                { key: "pickup", label: "Pickup" },
              ] as { key: DeliveryMethod; label: string }[]
            ).map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  deliveryFilter === tab.key
                    ? "border-orange-500 text-orange-500"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => {
                  setDeliveryFilter((prev) =>
                    prev === tab.key ? "all" : tab.key,
                  );
                  setPage(1);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <form onSubmit={handleSearch} className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder="Search by email..."
                value={searchEmail}
                onChange={(e) => {
                  setSearchEmail(e.target.value);
                  if (!e.target.value.trim()) {
                    setPage(1);
                  }
                }}
                className="w-full pl-4 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-sm outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600"
              />
              <button
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <IoSearchOutline className="w-5 h-5" />
              </button>
            </form>

            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-gray-900">
                  Status:
                </label>
                <div className="relative w-44">
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as AdminStatus | "");
                      setPage(1);
                    }}
                    className="w-full pl-3 pr-8 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 appearance-none"
                  >
                    {ADMIN_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="border border-gray-100 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left min-w-[1000px]">
                <thead className="bg-gray-50 border-b border-gray-100 text-gray-600">
                  <tr>
                    <th className="px-5 py-4 font-medium">S/N</th>
                    <th className="px-5 py-4 font-medium">Client Name</th>
                    <th className="px-5 py-4 font-medium">Email</th>
                    <th className="px-5 py-4 font-medium">Order #</th>
                    <th className="px-5 py-4 font-medium">Type</th>
                    <th className="px-5 py-4 font-medium">Delivery</th>
                    <th className="px-5 py-4 font-medium">Items</th>
                    <th className="px-5 py-4 font-medium">Order Date</th>
                    <th className="px-5 py-4 font-medium">Delivery Date</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 font-medium">Details</th>
                    <th className="px-5 py-4 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orders.status === "loading" ? (
                    <tr>
                      <td colSpan={12} className="px-5 py-12 text-center">
                        <LoadingSpinner />
                      </td>
                    </tr>
                  ) : filteredOrders.length === 0 ? (
                    <tr>
                      <td
                        colSpan={12}
                        className="px-5 py-12 text-center text-gray-500 italic"
                      >
                        {orders.data.length === 0
                          ? "No orders found."
                          : "No orders match your filters."}
                      </td>
                    </tr>
                  ) : (
                    displayOrders.map((order, idx) => (
                      <OrderRow
                        key={order._id}
                        order={order}
                        index={(page - 1) * ITEMS_PER_PAGE + idx + 1}
                        onViewDetails={handleViewDetails}
                        onCopyEmail={copyEmail}
                        onStatusUpdate={handleStatusUpdate}
                        statusDropdownOpen={statusDropdownOrderId === order._id}
                        onToggleDropdown={(id) =>
                          setStatusDropdownOrderId((prev) =>
                            prev === id ? null : id,
                          )
                        }
                        isUpdating={updateStatus.status === "loading"}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {orders.status === "success" && orders.pagination.total > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-500">
                Showing {showingStart} - {showingEnd} of {showingTotal} orders
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {detailModalOpen && (
        <OrderDetailModal
          order={singleOrder.data}
          loading={singleOrder.status === "loading"}
          onClose={() => {
            setDetailModalOpen(false);
            dispatch(clearSingleOrder());
          }}
        />
      )}
    </main>
  );
};

const StatCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  loading: boolean;
}> = ({ label, value, icon, loading }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm flex items-start justify-between">
    <div>
      <p className="text-sm font-medium text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">
        {loading ? "..." : value.toLocaleString()}
      </p>
    </div>
    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-600">
      {icon}
    </div>
  </div>
);

const STATUS_TRANSITIONS: Record<string, AdminStatus[]> = {
  pending: ["processing", "cancelled"],
  processing: ["readyForApproval", "cancelled"],
  readyForApproval: ["cancelled"],
  approved: [],
  cancelled: [],
};

const OrderRow: React.FC<{
  order: OrderProcessingOrder;
  index: number;
  onViewDetails: (id: string) => void;
  onCopyEmail: (email: string) => void;
  onStatusUpdate: (id: string, status: AdminStatus) => void;
  statusDropdownOpen: boolean;
  onToggleDropdown: (id: string) => void;
  isUpdating: boolean;
}> = ({
  order,
  index,
  onViewDetails,
  onCopyEmail,
  onStatusUpdate,
  statusDropdownOpen,
  onToggleDropdown,
  isUpdating,
}) => {
  const clientName =
    `${order.userId?.firstName ?? ""} ${order.userId?.lastName ?? ""}`.trim() ||
    "N/A";
  const email = order.userId?.email ?? "N/A";
  const displayStatus = getOrderDisplayStatus(order);
  const nextStatuses = STATUS_TRANSITIONS[order.adminStatus] ?? [];

  return (
    <tr className="hover:bg-gray-50/50 transition-colors">
      <td className="px-5 py-4 font-medium text-gray-900">{index}</td>
      <td className="px-5 py-4 text-gray-700 whitespace-nowrap">
        {clientName}
      </td>
      <td className="px-5 py-4 text-gray-600">
        <button
          type="button"
          onClick={() => onCopyEmail(email)}
          className="inline-flex items-center gap-1.5 hover:text-gray-900 transition-colors"
          title="Copy email"
        >
          <IoCopyOutline className="text-gray-400 shrink-0 w-4 h-4" />
          <span className="truncate max-w-[180px]">{email}</span>
        </button>
      </td>
      <td className="px-5 py-4 text-gray-600 font-mono text-xs whitespace-nowrap">
        {order.orderNumber}
      </td>
      <td className="px-5 py-4 text-gray-600 capitalize">{order.orderType}</td>
      <td className="px-5 py-4">
        {order.customerMode?.toLowerCase() === "pickup" ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-orange-50 text-orange-600">
            Pickup
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-600">
            Home Delivery
          </span>
        )}
      </td>
      <td className="px-5 py-4 text-gray-600">
        {order.orderItems?.length ?? 0}
      </td>
      <td className="px-5 py-4 text-gray-600 whitespace-nowrap">
        {formatDate(order.orderDate)}
      </td>
      <td className="px-5 py-4 text-gray-600 whitespace-nowrap">
        {formatDate(order.deliveryDateOption)}
      </td>
      <td className="px-5 py-4">
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${statusPillClass(displayStatus)}`}
        >
          {statusLabel(displayStatus)}
        </span>
      </td>
      <td className="px-5 py-4">
        <button
          type="button"
          onClick={() => onViewDetails(order._id)}
          className="bg-green-700 text-white px-3 py-1.5 rounded-md text-xs font-semibold hover:bg-green-800 transition-colors"
        >
          View Details
        </button>
      </td>
      <td className="px-5 py-4">
        {nextStatuses.length > 0 ? (
          <div className="relative">
            <button
              type="button"
              onClick={() => onToggleDropdown(order._id)}
              disabled={isUpdating}
              className="text-xs font-medium px-3 py-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 transition-colors flex items-center gap-1 disabled:opacity-50"
            >
              Update
              <FiChevronDown className="w-3 h-3" />
            </button>
            {statusDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[160px] py-1">
                {nextStatuses.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onStatusUpdate(order._id, s)}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                  >
                    {statusLabel(s)}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400 italic">No actions</span>
        )}
      </td>
    </tr>
  );
};

const OrderDetailModal: React.FC<{
  order: OrderProcessingOrder | null;
  loading: boolean;
  onClose: () => void;
}> = ({ order, loading, onClose }) => {
  const gamifiedDiscount = computeGamifiedDiscount(order);
  const freeDeliveryApplied = hasFreeDelivery(order);
  const displayStatus = order ? getOrderDisplayStatus(order) : "pending";
  const itemsSubtotal = order
    ? order.orderItems.reduce((sum, item) => sum + item.totalPrice, 0)
    : 0;
  const baseTotal = order
    ? itemsSubtotal + order.deliveryFee + order.serviceFee - order.discount
    : 0;
  const gamifiedDeliveryDiscount =
    order && freeDeliveryApplied ? order.deliveryFee : 0;
  const grandTotal =
    baseTotal - gamifiedDiscount - (gamifiedDeliveryDiscount ?? 0);
  const gamifiedDisplayItems = (order?.gamified ?? []).filter((g) => {
    if (g.isFreeDeliveryApplied) return false; // rendered as its own line
    if (g.rewardType === "discount") return (g.discountAmountApplied ?? 0) > 0;
    if (g.rewardType === "promocode") return Boolean(g.promoCode);
    if (g.rewardType === "checkout_category") {
      return Boolean(g.checkoutCategotyReward || g.checkoutCategoty);
    }
    return false;
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-[2px]">
      <div
        className="relative w-full max-w-3xl rounded-2xl bg-white shadow-xl max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 z-10"
          aria-label="Close"
        >
          <IoCloseOutline className="h-6 w-6" />
        </button>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner />
          </div>
        ) : !order ? (
          <div className="p-8 text-center text-gray-500">Order not found.</div>
        ) : (
          <div className="p-6 sm:p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Order Details
              </h2>
              <p className="text-sm text-gray-500 mt-1">{order.orderNumber}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                  Customer
                </h3>
                <div className="text-sm text-gray-700 space-y-1">
                  <p className="font-medium">
                    {order.userId?.firstName} {order.userId?.lastName}
                  </p>
                  <p>{order.userId?.email}</p>
                  <p>{order.userId?.phoneNumber}</p>

                  {(() => {
                    const contacts =
                      order?.deliveryContact2 &&
                      order.deliveryContact2.length > 0
                        ? order.deliveryContact2
                        : order?.userId?.deliveryContact2;

                    if (
                      !contacts ||
                      !Array.isArray(contacts) ||
                      contacts.length === 0
                    )
                      return null;

                    return (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <p className="font-medium text-gray-900 mb-1">
                          Additional Contacts:
                        </p>
                        <ul className="space-y-1 list-disc list-inside pl-1 text-gray-600">
                          {contacts.map((phone: string, idx: number) => (
                            <li key={idx}>
                              <span>{phone}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                  Delivery
                </h3>
                <div className="text-sm text-gray-700 space-y-1">
                  <p>{order.deliveryDetails || "N/A"}</p>
                  {order.pickUpDetails && (
                    <p className="text-gray-500">
                      Pickup: {order.pickUpDetails}
                    </p>
                  )}
                  <p>Delivery Date: {formatDate(order.deliveryDateOption)}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mb-6">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusPillClass(displayStatus)}`}
              >
                {statusLabel(displayStatus)}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 capitalize">
                {order.orderType}
              </span>
              {order.customerMode?.toLowerCase() === "pickup" ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-700">
                  Pickup
                </span>
              ) : (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                  Home Delivery
                </span>
              )}
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                Ordered: {formatDate(order.orderDate)}
              </span>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                Items ({order.orderItems.length})
              </h3>
              <div className="border border-gray-100 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">
                        Product
                      </th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {order.orderItems.map((item) => (
                      <tr key={item._id}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt=""
                                className="w-10 h-10 rounded-md border border-gray-100 object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-md border border-gray-100 bg-gray-50 flex items-center justify-center text-gray-400">
                                <IoImageOutline className="w-5 h-5" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">
                                {item.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {item.measurement}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900 font-medium">
                          {formatCurrency(item.totalPrice)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>{formatCurrency(itemsSubtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Delivery Fee</span>
                <span>{formatCurrency(order.deliveryFee)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Service Fee</span>
                <span>{formatCurrency(order.serviceFee)}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-{formatCurrency(order.discount)}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold text-gray-900">
                <span>Total</span>
                <span>{formatCurrency(Math.max(0, baseTotal))}</span>
              </div>

              {(freeDeliveryApplied || gamifiedDisplayItems.length > 0) && (
                <div className="pt-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Gamified
                  </div>
                  <div className="space-y-1.5">
                    {freeDeliveryApplied && (
                      <div className="flex justify-between gap-4 text-gray-600">
                        <span className="min-w-0 truncate">Free delivery</span>
                        <span className="shrink-0 text-right">
                          -{formatCurrency(order.deliveryFee)}
                        </span>
                      </div>
                    )}
                    {gamifiedDisplayItems.map((g) => (
                      <div
                        key={g._id}
                        className="flex justify-between gap-4 text-green-600"
                      >
                        <span className="min-w-0 truncate">
                          {g.rewardType === "promocode"
                            ? `Promo Code: ${g.promoCode}`
                            : g.rewardType === "discount"
                              ? "Gamified Discount"
                              : "Gamified Reward"}
                        </span>
                        <span className="shrink-0 text-right">
                          {g.rewardType === "promocode"
                            ? `-${formatCurrency(g.discountAmountApplied ?? 0)}`
                            : g.rewardType === "discount" &&
                                g.discountAmountApplied != null
                              ? `-${formatCurrency(g.discountAmountApplied)}`
                              : g.rewardType === "checkout_category"
                                ? g.checkoutCategotyReward ||
                                  g.checkoutCategoty ||
                                  "Applied"
                                : "Applied"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold text-gray-900">
                <span>Grand Total</span>
                <span>{formatCurrency(Math.max(0, grandTotal))}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderProcessing;
